import { useState } from 'react';
import { Page, Modal, BlockStack } from '@shopify/polaris';
import { useResource } from '../../../shared/hooks/useResource';
import { useResourceMutation } from '../../../shared/hooks/useResourceMutation';
import { ErrorBanner } from '../../../shared/components/ErrorBanner';
import { DataTable } from '../../../shared/components/DataTable';
import { ConfirmDestructiveAction } from '../../../shared/components/ConfirmDestructiveAction';
import { FallbackRateForm } from './FallbackRateForm';
import { fallbackRateToRow } from './FallbackRateRow';
import {
  FallbackRate,
  FallbackRateInput,
  listFallbackRates,
  createFallbackRate,
  updateFallbackRate,
  deleteFallbackRate,
} from './api';

const COLUMNS = [
  { id: 'shop', title: 'Shop' },
  { id: 'pattern', title: 'Postcode pattern' },
  { id: 'weight', title: 'Weight range' },
  { id: 'price', title: 'Price' },
  { id: 'service', title: 'Service' },
  { id: 'actions', title: 'Actions' },
];

export function FallbackRatesPage(): JSX.Element {
  const query = useResource(['fallback-rates'], listFallbackRates);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<FallbackRate | null>(null);
  const [deleting, setDeleting] = useState<FallbackRate | null>(null);

  const createMutation = useResourceMutation(createFallbackRate, [['fallback-rates']]);
  const updateMutation = useResourceMutation(updateFallbackRate, [['fallback-rates']]);
  const deleteMutation = useResourceMutation(deleteFallbackRate, [['fallback-rates']]);

  function handleCreate(input: FallbackRateInput): void {
    createMutation.mutate(input, { onSuccess: () => setShowAdd(false) });
  }

  function handleUpdate(input: FallbackRateInput): void {
    if (!editing) return;
    const { shopDomain: _ignored, ...patch } = input;
    updateMutation.mutate(
      { id: editing.id, patch },
      { onSuccess: () => setEditing(null) },
    );
  }

  function handleDelete(): void {
    if (!deleting) return;
    deleteMutation.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
  }

  const rows =
    query.data?.items.map((r) =>
      fallbackRateToRow(r, {
        onEdit: setEditing,
        onDelete: setDeleting,
      }),
    ) ?? [];

  return (
    <Page
      title="Fallback rates"
      subtitle="Used when TNT is unreachable or returns no quote"
      primaryAction={{ content: 'Add fallback rate', onAction: () => setShowAdd(true) }}
    >
      <BlockStack gap="400">
        {query.error ? <ErrorBanner error={query.error} /> : null}
        {createMutation.error ? <ErrorBanner error={createMutation.error} /> : null}
        {updateMutation.error ? <ErrorBanner error={updateMutation.error} /> : null}
        {deleteMutation.error ? <ErrorBanner error={deleteMutation.error} /> : null}

        <DataTable
          columns={COLUMNS}
          rows={rows}
          loading={query.isLoading}
          resourceName={{ singular: 'rate', plural: 'rates' }}
          emptyState={{ heading: 'No fallback rates' }}
        />
      </BlockStack>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add fallback rate">
        <Modal.Section>
          <FallbackRateForm
            onSubmit={handleCreate}
            onCancel={() => setShowAdd(false)}
            submitting={createMutation.isPending}
          />
        </Modal.Section>
      </Modal>

      <Modal open={editing !== null} onClose={() => setEditing(null)} title="Edit fallback rate">
        <Modal.Section>
          {editing ? (
            <FallbackRateForm
              initial={{
                shopDomain: editing.shop?.domain ?? '',
                postcodePattern: editing.postcodePattern,
                minWeightG: editing.minWeightG,
                maxWeightG: editing.maxWeightG,
                priceMinorUnits: editing.priceMinorUnits,
                currency: editing.currency,
                serviceName: editing.serviceName,
              }}
              onSubmit={handleUpdate}
              onCancel={() => setEditing(null)}
              submitting={updateMutation.isPending}
            />
          ) : null}
        </Modal.Section>
      </Modal>

      <ConfirmDestructiveAction
        open={deleting !== null}
        title="Delete fallback rate"
        message={`Delete fallback rate for "${deleting?.postcodePattern ?? ''}"? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
        loading={deleteMutation.isPending}
      />
    </Page>
  );
}
